import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, Flex, Spinner, Text } from "@chakra-ui/react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { AppBar, APP } from "../../components/mobileUi";
import {
  DesktopBack,
  DesktopPage,
  DesktopPanel,
  useIsDesktop,
} from "../../components/desktopUi";
import {
  getIncomeProvisionalDetailApi,
  getIncomeComparativeDetailApi,
  getIncomePaidDetailApi,
  getViewAsProvisionalDetailApi,
  getViewAsComparativeDetailApi,
  getViewAsPaidDetailApi,
} from "../../api/finance.income.api";
import { useEmployeeAuth } from "../../auth/EmployeeAuthContext";
import { EMP_BASE_PATH } from "../../config";
import { OverviewTab, DetailTab, SummaryTab, DisbursementTab } from "./IncomeDetailTabs";

const MUTED = "#94A3B8";
const BASE600 = "#C8C8C8";

// Ở chế độ "Kiểm tra giao dịch" (có employeeCode) app dùng bộ endpoint
// checkTransaction/* thay cho endpoint thu nhập cá nhân.
const KIND = {
  "tam-tinh": {
    title: "Chi tiết giao dịch",
    api: getIncomeProvisionalDetailApi,
    viewAsApi: getViewAsProvisionalDetailApi,
    kindKey: "provisional",
  },
  "doi-chieu": {
    title: "Chi tiết giao dịch",
    api: getIncomeComparativeDetailApi,
    viewAsApi: getViewAsComparativeDetailApi,
    kindKey: "comparative",
  },
  "chi-tra": {
    title: "Chi tiết giao dịch",
    api: getIncomePaidDetailApi,
    viewAsApi: getViewAsPaidDetailApi,
    kindKey: "paid",
  },
};

const IncomeDetail = () => {
  const isDesktop = useIsDesktop();
  const navigate = useNavigate();
  const { kind, id } = useParams();
  const [search] = useSearchParams();
  const employeeCode = search.get("employeeCode") || "";
  const apartmentCode = search.get("apartmentCode") || "";
  const { user } = useEmployeeAuth();
  const cfg = KIND[kind] || KIND["tam-tinh"];

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setError(false);
    const request = employeeCode
      ? cfg.viewAsApi(id, { employeeCode, ...(apartmentCode ? { apartmentCode } : {}) })
      : cfg.api(id);
    request
      .then((d) => setData(d))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [id, cfg, employeeCode, apartmentCode]);

  const routes = useMemo(() => {
    const base = [
      { key: "overview", title: "Thông tin chung" },
      { key: "detail", title: "Chi tiết" },
      { key: "participant", title: "Tổng hợp" },
    ];
    if (cfg.kindKey === "paid") base.push({ key: "disbursement", title: "Thanh toán" });
    return base;
  }, [cfg.kindKey]);

  useEffect(() => { if (tabIndex >= routes.length) setTabIndex(0); }, [routes.length, tabIndex]);

  const lk = data?.locking || {};
  const lockingId = data?.lockingId || lk?._id;
  const isBooker = user?._id && lk?.bookerId && user._id === lk.bookerId;
  const canUpdateRelative =
    cfg.kindKey === "provisional" &&
    isBooker &&
    lk?.status === "APPROVED" &&
    lk?.registerRelativeStatus !== "APPROVED";

  const activeKey = routes[tabIndex]?.key;

  const pageBody = (
    <>

      {loading ? (
        <Flex justify="center" py="60px"><Spinner color={APP.primary200} /></Flex>
      ) : error || !data ? (
        <Flex justify="center" py="60px"><Text fontSize="14px" color={MUTED}>Không tìm thấy thông tin locking</Text></Flex>
      ) : (
        <>
          {/* Tab bar */}
          <Flex borderBottom="1px solid #EEF1F6">
            {routes.map((r, i) => {
              const active = i === tabIndex;
              return (
                <Flex
                  as="button"
                  type="button"
                  key={r.key}
                  flex="1"
                  direction="column"
                  align="center"
                  justify="center"
                  minH="46px"
                  px="4px"
                  onClick={() => setTabIndex(i)}
                  borderBottom="2px solid"
                  borderColor={active ? APP.primary200 : "transparent"}
                >
                  <Text fontSize="13px" fontWeight={600} color={active ? APP.primary200 : BASE600} textAlign="center" lineHeight="1.2">
                    {r.title}
                  </Text>
                </Flex>
              );
            })}
          </Flex>

          {/* Tab content */}
          <Box flex="1" overflowY="auto">
            {activeKey === "overview" && <OverviewTab data={data} kind={cfg.kindKey} user={user} navigate={navigate} />}
            {activeKey === "detail" && <DetailTab data={data} kind={cfg.kindKey} user={user} />}
            {activeKey === "participant" && <SummaryTab data={data} />}
            {activeKey === "disbursement" && <DisbursementTab data={data} />}
          </Box>

          {activeKey === "detail" && canUpdateRelative && (
            <Box borderTop="1px solid #EEF1F6" p="16px">
              <Button
                w="100%"
                h="48px"
                bg={APP.primary200}
                color="white"
                borderRadius="8px"
                _hover={{ bg: APP.primary100 }}
                onClick={() => navigate(`${EMP_BASE_PATH}/tai-chinh/locking/${lockingId}/ctv`)}
              >
                Cập nhật CTV tham gia
              </Button>
            </Box>
          )}
        </>
      )}
    </>
  );

  // ===================== Bản WEB (desktop) =====================
  if (isDesktop) {
    return (
      <DesktopPage title={cfg.title} right={<DesktopBack onClick={() => navigate(-1)} />}>
        <DesktopPanel p="0" pb="24px">
          {pageBody}
        </DesktopPanel>
      </DesktopPage>
    );
  }

  return (
    <Box maxW="480px" mx="auto" bg="white" minH="100dvh" display="flex" flexDirection="column">
      {/* Back mặc định = quay lại màn trước (khớp router.back của app): màn này còn
          được mở từ Quản lý thu nhập nên không được điều hướng cứng về danh sách. */}
      <AppBar title={cfg.title} />
      {pageBody}
    </Box>
  );
};

export default IncomeDetail;
